import { Button, Card, Form, FormButtonRow, FormGroup, FormParagraph, Link, PageHeader } from '@ksyos/design-system';
import { useNavigate, useParams } from '@ksyos/router';
import { uniqBy } from 'lodash';
import { useEffect, useState } from 'react';
import { ManagerAuthorization, ToastType } from '@common/enums';
import { UrlBuilder } from '@common/utils/urlBuilder';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { fetchJson } from '@frontend/store/fetch';
import { assertHasAuthorization, showToast } from '@frontend/utils';
import type { ValidationErrors } from '../../../utils/validation';
import type { IOrganization } from '../../interfaces/organization';
import { DeviceAccessoryStatus } from '../../../../common/enums/deviceAccessoryStatus';
import { deviceAccessoryAddSchema, deviceAccessoryEditSchema } from '../../../../common/validation/deviceAccessory';
import { getSelectedLanguage, translateKey } from '../../../translate';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../utils/validation';
import { DropdownSearch, EnumDropdown, NullableInputText } from '../../elements/common';

interface IService {
    code: string;
    id: string;
}

interface IAddEditAccessoryDeviceType {
    consumingServices: IService[];
    id: string;
    manufacturer: string;
    model: string;
    name: string;
    providingServices: IService[];
    version: string;
}

interface IAddEditAccessoryDeviceAccessoryType {
    deviceType: IAddEditAccessoryDeviceType;
    deviceTypeId: string;
    id: string;
    name: string;
}

interface IAddEditAccessoryOrganization {
    id: string;
    name: string;
}

interface IAddEditDeviceAccessory {
    deviceAccessoryOrganization: {
        details: string | null;
        issueDate: string | null;
        organization: IAddEditAccessoryOrganization | null;
    } | null;
    deviceAccessoryType: IAddEditAccessoryDeviceAccessoryType | null;
    deviceAccessoryTypeId: string | null;
    id?: string;
    organizationId: string | null;
    referenceNumber: string | null;
    serialNumber: string | null;
    status: DeviceAccessoryStatus | null;
}

const emptyDeviceAccessory: IAddEditDeviceAccessory = {
    deviceAccessoryType: null,
    deviceAccessoryTypeId: null,
    referenceNumber: null,
    serialNumber: null,
    status: DeviceAccessoryStatus.inStock,
    organizationId: null,
    deviceAccessoryOrganization: {
        details: null,
        issueDate: null,
        organization: null,
    },
};

export function DeviceAccessoriesAddEdit() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [accessory, setAccessory] = useState<IAddEditDeviceAccessory>(emptyDeviceAccessory);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [devicesTypes, setDevicesTypes] = useState<Map<string, IAddEditAccessoryDeviceType>>(new Map());
    const [deviceAccessoryTypes, setDeviceAccessoryTypes] = useState<Map<string, IAddEditAccessoryDeviceAccessoryType>>(
        new Map()
    );

    const [isSaving, setIsSaving] = useState<boolean>(false);
    const translate = getSelectedLanguage();

    assertHasAuthorization(ManagerAuthorization.devicesAddEdit);

    useEffect(() => {
        loadDeviceTypes();
        loadDeviceAccessoryTypes();

        if (id) {
            loadDeviceAccessory();
        }
    }, []);

    const renderOrganizationSelector = () => (
        <FormGroup
            hasErrors={errors.has('organizationId')}
            isRequired
            key={'organizationId'}
            label={translateKey('organization.organization')}
        >
            {id && accessory.deviceAccessoryOrganization ? (
                <>
                    <FormParagraph>{accessory.deviceAccessoryOrganization?.organization?.name}</FormParagraph>
                    <Link
                        iconLeft="close"
                        onClick={() => {
                            const updatedAccessory = {
                                ...accessory,
                                status: DeviceAccessoryStatus.inStock,
                                organizationId: null,
                                organization: null,
                                deviceAccessoryOrganization: null,
                            };

                            setAccessory(updatedAccessory);
                        }}
                        sx={{ mt: 1 }}
                        variant="button"
                    >
                        {translateKey('deviceAccessories.removeFromOrganization')}
                    </Link>
                </>
            ) : (
                <DropdownSearch
                    clearable={true}
                    error={errors.get('organizationId')}
                    loadOptions={loadOrganizations}
                    onChange={(value) => {
                        const updatedAccessory = {
                            ...accessory,
                            organizationId: value,
                            deviceAccessoryOrganization: null,
                        };
                        setAccessory(updatedAccessory);
                        validateOne(updatedAccessory, 'organizationId');
                    }}
                    placeholder={translateKey('organization.nameOrTxcsId')}
                    value={accessory.organizationId}
                />
            )}
        </FormGroup>
    );

    return (
        <PageTemplate width="medium">
            <PageHeader title={id ? translateKey('deviceAccessories.edit') : translateKey('deviceAccessories.add')} />
            <Card>
                <Card.Header title={translateKey('deviceAccessories.details')} />
                <Card.Body>
                    <Form
                        onSubmit={async (event) => {
                            event.preventDefault();
                            await save();
                        }}
                        submitOnEnter={true}
                    >
                        <FormGroup
                            hasErrors={errors.has('deviceAccessoryTypeId')}
                            isRequired
                            label={translateKey('deviceAccessories.type')}
                        >
                            <DropdownSearch
                                clearable={false}
                                disabled={!!accessory.id}
                                error={errors.get('deviceAccessoryTypeId')}
                                loadOptions={loadAccessoryTypes}
                                onChange={(value) => {
                                    const updatedAccessory = {
                                        ...accessory,
                                        deviceAccessoryTypeId: value,
                                        deviceAccessoryType: value ? deviceAccessoryTypes.get(value)! : null,
                                    };

                                    setAccessory(updatedAccessory);
                                    validateOne(updatedAccessory, 'deviceAccessoryTypeId');
                                }}
                                value={accessory.deviceAccessoryTypeId}
                            />
                        </FormGroup>

                        <FormGroup
                            hasErrors={errors.has('referenceNumber')}
                            isRequired={!accessory.serialNumber}
                            label={translateKey('deviceAccessories.referenceNumber')}
                        >
                            <NullableInputText
                                disabled={!!accessory.id}
                                error={errors.get('referenceNumber')}
                                onChange={(value) => {
                                    const updatedAccessory = {
                                        ...accessory,
                                        referenceNumber: value,
                                    };

                                    setAccessory(updatedAccessory);
                                    setErrors(validateBatchAndSerialNumbers(updatedAccessory, errors));
                                }}
                                placeholder={translateKey('deviceAccessories.referenceNumber')}
                                value={accessory.referenceNumber}
                            />
                        </FormGroup>

                        <FormGroup
                            hasErrors={errors.has('serialNumber')}
                            isRequired={!accessory.referenceNumber}
                            label={translateKey('deviceAccessories.serialNumber')}
                        >
                            <NullableInputText
                                disabled={!!accessory.id}
                                error={errors.get('serialNumber')}
                                onChange={(value) => {
                                    const updatedAccessory = {
                                        ...accessory,
                                        serialNumber: value,
                                    };

                                    setAccessory(updatedAccessory);
                                    setErrors(validateBatchAndSerialNumbers(updatedAccessory, errors));
                                }}
                                placeholder={translateKey('deviceAccessories.serialNumber')}
                                value={accessory.serialNumber}
                            />
                        </FormGroup>

                        <FormGroup
                            hasErrors={errors.has('status')}
                            isRequired
                            label={translateKey('common.status')}
                        >
                            <EnumDropdown
                                className="automation-device-accessories-add-edit-select-status"
                                disabled={!!accessory.organizationId}
                                enumName={'deviceAccessoryStatus'}
                                error={errors.get('status')}
                                jsEnum={DeviceAccessoryStatus}
                                onChange={(value) => {
                                    const updatedAccessory = {
                                        ...accessory,
                                        status: value,
                                    };
                                    setAccessory(updatedAccessory);
                                    validateOne(updatedAccessory, 'status');
                                }}
                                value={accessory.status}
                            />
                        </FormGroup>

                        {accessory.deviceAccessoryTypeId &&
                            accessory.status === DeviceAccessoryStatus.sent &&
                            renderOrganizationSelector()}
                        <FormButtonRow>
                            <Button
                                isDisabled={isSaving}
                                onClick={() => navigate('/devices/accessories')}
                            >
                                {translateKey('common.cancel')}
                            </Button>
                            <Button
                                isDisabled={isSaving}
                                type="submit"
                            >
                                {translateKey('common.save')}
                            </Button>
                        </FormButtonRow>
                    </Form>
                </Card.Body>
            </Card>
        </PageTemplate>
    );

    function organizationsToDropdownOptions(organizations: IOrganization[]) {
        return organizations.map((organization) => ({
            value: organization.id,
            label: `${organization.name} ${organization.txcsId}`,
        }));
    }

    async function fetchOrganizations(
        getServiceConsumer: boolean,
        serviceIds: string[],
        searchText?: string
    ): Promise<IOrganization[]> {
        const serviceListEndpoint = getServiceConsumer ? 'serviceConsumersList' : 'serviceProvidersList';

        const urlBuilder = new UrlBuilder(`/api/${serviceListEndpoint}/${serviceIds.join(',')}/organizations`);

        if (searchText) {
            urlBuilder.add('searchText', searchText);
        }

        const url = urlBuilder.build();
        const response = await fetchJson(url, 'GET');

        return response.json.items;
    }

    async function loadConsumerOrganizations(serviceIds: string[], searchText?: string): Promise<IOrganization[]> {
        return fetchOrganizations(true, serviceIds, searchText);
    }

    async function loadProviderOrganizations(serviceIds: string[], searchText?: string): Promise<IOrganization[]> {
        return fetchOrganizations(false, serviceIds, searchText);
    }

    async function loadOrganizations(searchText: string) {
        const deviceType = accessory.deviceAccessoryType
            ? devicesTypes.get(accessory.deviceAccessoryType.deviceTypeId)
            : null;

        if (!deviceType) {
            return [];
        }

        const result = [];

        const consumingServicesIds = deviceType.consumingServices.map((consumingService) => consumingService.id);

        if (consumingServicesIds.length > 0) {
            const consumingServicesOrganizations = await loadConsumerOrganizations(consumingServicesIds, searchText);

            result.push(...organizationsToDropdownOptions(consumingServicesOrganizations));
        }

        const providingServicesIds = deviceType.providingServices.map((providingService) => providingService.id);

        if (providingServicesIds.length > 0) {
            const providingServicesOrganizations = await loadProviderOrganizations(providingServicesIds, searchText);

            result.push(...organizationsToDropdownOptions(providingServicesOrganizations));
        }

        return uniqBy(result, 'value');
    }

    async function loadAccessoryTypes(searchText: string) {
        const urlBuilder = new UrlBuilder('/api/deviceAccessoryTypes');

        if (searchText) {
            urlBuilder.add('searchText', searchText);
        }

        const url = urlBuilder.build();

        const response = await fetchJson(url, 'GET');

        return (response.json.items as IAddEditAccessoryDeviceAccessoryType[]).map((deviceAccessoryType) => ({
            value: deviceAccessoryType.id,
            label: deviceAccessoryType.name,
        }));
    }

    async function loadDeviceAccessory() {
        const result = await fetchJson(`/api/devices/accessories/${id}`, 'GET');

        setAccessory({ ...result.json, organizationId: result.json.deviceAccessoryOrganization?.organization?.id });
    }

    async function loadDeviceTypes() {
        const result = await fetchJson('/api/deviceTypes', 'GET');

        setDevicesTypes(new Map(result.json.items.map((item: IAddEditAccessoryDeviceType) => [item.id, item])));
    }

    async function loadDeviceAccessoryTypes() {
        const result = await fetchJson('/api/deviceAccessoryTypes', 'GET');

        setDeviceAccessoryTypes(
            new Map(result.json.items.map((item: IAddEditAccessoryDeviceAccessoryType) => [item.id, item]))
        );
    }

    async function validateOne(accessoryToValidate: IAddEditDeviceAccessory, name: keyof IAddEditDeviceAccessory) {
        let validationErrors = updateValidationErrorsForOne(
            deviceAccessoryAddSchema,
            accessoryToValidate,
            errors,
            name
        );

        if (name === 'organizationId') {
            validationErrors = validateOrganizationAndGroupId(accessoryToValidate, validationErrors);
        }

        setErrors(validationErrors);
    }

    function validateBatchAndSerialNumbers(
        accessoryToValidate: IAddEditDeviceAccessory,
        validationErrors: ValidationErrors
    ): ValidationErrors {
        if (!accessoryToValidate.serialNumber && !accessoryToValidate.referenceNumber) {
            return validationErrors.set('serialNumber', 'any_required').set('referenceNumber', 'any_required');
        } else {
            const validationErrorsResult = validationErrors.remove('serialNumber').remove('referenceNumber');

            return validationErrorsResult.concat(
                emptyValidationErrors
                    .concat(
                        updateValidationErrorsForOne(
                            deviceAccessoryAddSchema,
                            accessoryToValidate,
                            validationErrorsResult,
                            'serialNumber'
                        )
                    )
                    .concat(
                        updateValidationErrorsForOne(
                            deviceAccessoryAddSchema,
                            accessoryToValidate,
                            validationErrorsResult,
                            'referenceNumber'
                        )
                    )
            ) as ValidationErrors;
        }
    }

    function validateOrganizationAndGroupId(
        accessoryToValidate: IAddEditDeviceAccessory,
        validationErrors: ValidationErrors
    ): ValidationErrors {
        const errorKey = 'organizationId';
        if (accessoryToValidate.status === DeviceAccessoryStatus.sent) {
            if (!accessoryToValidate.organizationId) {
                return validationErrors.set(errorKey, 'string_empty');
            }
        } else {
            return validationErrors.remove(errorKey);
        }

        return validationErrors;
    }

    function getCustomValidations(
        accessoryToValidate: IAddEditDeviceAccessory,
        validationErrors: ValidationErrors
    ): ValidationErrors {
        let newValidationErrors = validateOrganizationAndGroupId(accessoryToValidate, validationErrors);
        newValidationErrors = validateBatchAndSerialNumbers(accessoryToValidate, newValidationErrors);

        return newValidationErrors;
    }

    async function saveDeviceAccessory() {
        const validationResult = validate(
            accessory.id ? deviceAccessoryEditSchema : deviceAccessoryAddSchema,
            accessory
        );
        const validationErrors = getCustomValidations(accessory, validationResult.errors);
        setErrors(validationErrors);

        if (validationErrors.size > 0) {
            return false;
        }

        setIsSaving(true);

        const idForUrl = accessory.id ? `/${accessory.id}` : '';
        const result = await fetchJson(`/api/devices/accessories${idForUrl}`, 'POST', validationResult.item);

        if (result.errorCode) {
            setIsSaving(false);

            return false;
        }

        setIsSaving(false);
        showToast(ToastType.success, translate.devices.addDeviceAccessorySuccessMessage);

        return true;
    }

    async function save() {
        const success = await saveDeviceAccessory();

        if (success) {
            navigate('/devices/accessories');
        }
    }
}
